import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  getCommand,
  getCommandAndFileTimes,
  getCommandSpinner,
  getCommandStatus,
  getDownloadStatusText,
  getFile,
  getHealthcheckReportIcon,
  getOnlineStatusText,
  downloadStatusToIconSrc,
} from '../Devices/DeviceUtils';
import ToolTip from '../ToolTipIcon/ToolTip';
import { classNames } from '../../utils/v4/utils';
import DeviceUptimeDiagram from '../Devices/DeviceUptimeDiagram';

function getUptimeToolText(device) {
  const { heartbeatPercent, name } = device;

  return (
    <Popover
      id={`popover-screenshot-${name}`}
      className="popover-uptime-diagram"
      title={`${heartbeatPercent}% Uptime`}
    >
      <DeviceUptimeDiagram device={device} height={150} />
    </Popover>
  );
}

function getTableRows(props) {
  const {
    el,
    i,
    auth,
    handleDeviceSelectedChange,
    handleDiagnosticsClicked,
    handleScreenshotClicked,
    activeCampaignId,
    getUptimeButton,
    goToDevicesPage,
    selectedDevices,
  } = props;
  const isValidLocation = el.location_path && el.location_path !== '/';
  const locationDisplay = isValidLocation ? el.location_path : 'No Location set';
  const SCREENSHOT = 'screenshot';
  const screenshotFile = getFile(el, SCREENSHOT);
  const screenshotCommand = getCommand(el, SCREENSHOT);
  const screenshotCommandStatus = getCommandStatus(el, SCREENSHOT);
  const screenshotTime = getCommandAndFileTimes(el, SCREENSHOT);
  const screenshotSpinner = getCommandSpinner(el, SCREENSHOT);
  let screenshotToolText;
  let screenshotComponent;

  if (!screenshotFile) {
    screenshotToolText = (
      <div>
        Screenshot <span className="pull-right color-red-500">{screenshotCommandStatus}</span>
        {screenshotCommand && (
          <div className="device-request-time">
            <span>{screenshotTime.commandText}</span>
            {screenshotTime.commandTime}
          </div>
        )}
      </div>
    );
    screenshotComponent = (
      <ToolTip placement="bottom" toolText={screenshotToolText}>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleScreenshotClicked(el);
          }}
          className={classNames(
            'duration-500 border-2 p-2 hover:scale-105 cursor-pointer flex justify-center ml-2 items-center bg-white rounded-lg',
            screenshotFile ? '' : 'opacity-50',
          )}
        >
          <img
            src="/assets/images/icons/camera_button.svg"
            alt="Capture Screenshot"
            className="w-[20px] h-[20px]"
          />
        </button>
      </ToolTip>
    );
  } else {
    const fileUrl = `/cms/data/v1/devices/${el.id}/file/${screenshotFile.filename}?${screenshotFile.timestamp}`;
    const title = (
      <span>
        Screenshot
        <span className="pull-right rel">
          {screenshotCommandStatus && <span className="color-red-500">Request failed</span>}
          {screenshotSpinner}
        </span>
      </span>
    );

    screenshotToolText = (
      <Popover id={`popover-screenshot-${el.name}`} className="popover-screenshot" title={title}>
        {Boolean(screenshotTime.commandTime) && (
          <div className="device-request-time">
            <span>{screenshotTime.commandText}</span>
            {screenshotTime.commandTime}
          </div>
        )}
        <div className="device-request-time">
          <span>Uploaded:</span>
          {screenshotTime.fileTime}
        </div>
        <div className="device-screenshot">
          <img src={fileUrl} alt="Screenshot" />
        </div>
      </Popover>
    );
    screenshotComponent = (
      <OverlayTrigger trigger={['hover', 'focus']} placement="left" overlay={screenshotToolText}>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleScreenshotClicked(el);
          }}
          className={classNames(
            'duration-500 border-2 p-2 hover:scale-105 cursor-pointer flex justify-center ml-2 items-center bg-white rounded-lg',
            screenshotFile ? '' : 'opacity-50',
          )}
        >
          <img
            src="/assets/images/icons/camera_button.svg"
            alt="Capture Screenshot"
            className="w-[20px] h-[20px]"
          />
        </button>
      </OverlayTrigger>
    );
  }

  const DIAGNOSTICS = 'diagnostics';
  const diagnosticsCommand = getCommand(el, DIAGNOSTICS);
  const diagnosticsFile = getFile(el, DIAGNOSTICS);
  const diagnosticsCommandStatus = getCommandStatus(el, DIAGNOSTICS);
  const diagnosticsTime = getCommandAndFileTimes(el, DIAGNOSTICS);
  const diagnosticsToolText = (
    <div>
      Diagnostics <span className="pull-right color-red-500">{diagnosticsCommandStatus}</span>
      {diagnosticsCommand && (
        <div className="device-request-time">
          <span>{diagnosticsTime.commandText}</span>
          {diagnosticsTime.commandTime}
        </div>
      )}
      {diagnosticsFile && (
        <div className="device-request-time">
          <span>Uploaded:</span>
          {diagnosticsTime.fileTime}
        </div>
      )}
    </div>
  );
  const healthcheckIcon = getHealthcheckReportIcon(el, auth);

  return (
    <tr
      data-test={`device-row-${el.id}`}
      onClick={e => handleDeviceSelectedChange(e, el, i)}
      onDoubleClick={() => goToDevicesPage(el.id)}
      key={el.id || i}
      className={classNames(
        'w-full align-middle  xl:pt-[6px] xl:pb-[6px] cursor-pointer flex xl:flex-row flex-col mt-4 space-y-2 xl:space-y-0 border-b-2 xl:border-b-0 pb-2',
        selectedDevices[i] !== undefined ? 'bg-[#ff5f0036]' : '',
      )}
    >
      {/* DEVICE ID */}
      <td className="w-full xl:w-[200px] text-left px-2   flex flex-row space-x-2 xl:space-x-0 overflow-hidden">
        <p className="poppins-bold xl:w-0 xl:h-0 xl:overflow-hidden mb-0">Device Id: </p>
        <Link
          to={`/devices/${el.id}`}
          onClick={e => e.stopPropagation()}
          data-test={`device-table-id-${el.id}`}
          className="cursor-pointer flex items-center text-left poppins-bold text-[#FF5E00] hover:text-[#FF5E00] mb-0 truncate"
        >
          {el.name}
        </Link>
      </td>

      {/* DEVICE NAME */}
      <td className="xl:w-[calc(28%-200px)] text-left px-2 flex flex-row space-x-2 xl:space-x-0 break-words">
        <p className="poppins-bold xl:w-0 xl:h-0 xl:overflow-hidden mb-0 flex-shrink-0">
          Device Name:{' '}
        </p>
        <div className="flex flex-col gap-1 min-w-0">
          <p
            data-test={`device-table-description-${el.id}`}
            className="text-left poppins-regular text-[#3B6C81] mb-0"
          >
            {el.description}
          </p>
          {el.tags && el.tags.length > 0 && (
            <div data-test={`device-tags-${el.id}`} className="flex flex-wrap gap-1">
              {el.tags
                .filter(tag => tag.length > 0)
                .map(tag => (
                  <span
                    key={tag}
                    data-test={`device-tag-${el.id}`}
                    className="inline-flex items-center rounded-md bg-orange-50 px-2 py-1 text-xs font-medium text-sunrise inset-ring inset-ring-orange-600/20"
                  >
                    {tag}
                  </span>
                ))}
            </div>
          )}
        </div>
      </td>

      {/* LOCATION */}
      <td className="w-full xl:w-[20%] text-left px-2 flex flex-row space-x-2 xl:space-x-0">
        <p className="poppins-bold xl:w-0 xl:h-0 xl:overflow-hidden mb-0 flex-shrink-0">
          Location:{' '}
        </p>
        <p
          data-test={`device-location-${el.id}`}
          className="poppins-regular text-[#3B6C81] mb-0 truncate"
        >
          {locationDisplay}
        </p>
      </td>

      {/* PLAYER, DEVICE, FW */}
      <td className="w-full xl:w-[14%] text-left px-2 flex flex-row space-x-2 xl:space-x-0">
        <p className="poppins-bold xl:w-0 xl:h-0 xl:overflow-hidden mb-0 flex-shrink-0">
          Player, Device, FW:{' '}
        </p>
        <div className="flex flex-col gap-1 min-w-0">
          <p
            data-test={`device-table-version-${el.id}`}
            className="text-left poppins-regular text-[#3B6C81] mb-0"
          >
            {el.version}
          </p>
          {(el.device_type || el.firmware_version) && (
            <p className="poppins-regular text-[#3B6C81] mb-0 text-xs">
              {el.device_type}
              {el.device_type && el.firmware_version && <br />}
              {el.firmware_version}
            </p>
          )}
        </div>
      </td>

      {/* STATUS */}
      <td className="w-full xl:w-[16%] text-left px-2 flex justify-start items-center xl:items-start flex-row flex-wrap gap-2">
        <p className="poppins-bold xl:hidden mb-0">Status: </p>
        {healthcheckIcon && (
          <div className="border-2 p-2 flex justify-center items-center bg-white rounded-lg [&_.material-icons]:!text-[20px] [&_.material-icons]:!leading-none">
            {healthcheckIcon}
          </div>
        )}
        <ToolTip toolText={getOnlineStatusText(el)}>
          <div className="border-2 p-2 flex justify-center items-center bg-white rounded-lg page-dashboard">
            <span
              className={classNames(
                'tool-tip-icon material-icons !text-[20px]',
                !el.idle ? 'unknown' : el.online_status,
              )}
            >
              lens
            </span>
          </div>
        </ToolTip>
        <ToolTip toolText={getDownloadStatusText(el)}>
          <div className="border-2 p-2 flex justify-center items-center bg-white rounded-lg">
            <img
              data-test="icon-download-status"
              src={downloadStatusToIconSrc(el)}
              alt="downloading status"
              className={classNames(
                'w-[20px] h-[20px] inline-block',
                el.download_status === 'downloading' ? 'animate-pulse' : '',
                el.idle ? '' : 'opacity-20',
                el.download_status,
              )}
            />
          </div>
        </ToolTip>
        <OverlayTrigger
          trigger={['hover', 'focus']}
          placement="top"
          overlay={getUptimeToolText(el)}
        >
          <div className="border-2 p-2 flex justify-center items-center bg-white rounded-lg">
            <img
              data-test={`devices-table-health-status-${el.id}`}
              src={getUptimeButton(el)}
              alt="health status"
              className="w-[20px] h-[20px]"
            />
          </div>
        </OverlayTrigger>
      </td>

      {/* EDIT */}
      <td className="w-full xl:w-[24%] text-left px-2 flex justify-start items-center xl:items-start flex-row">
        <p className="poppins-bold xl:w-0 xl:h-0 xl:overflow-hidden mb-0">Edit: </p>
        <ToolTip toolText={diagnosticsToolText}>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              handleDiagnosticsClicked(el);
            }}
            className={classNames(
              'duration-500 hover:scale-105 cursor-pointer border-2 p-2 flex justify-center items-center bg-white rounded-lg ml-2 xl:ml-0',
              diagnosticsFile ? '' : 'opacity-50',
            )}
          >
            <img
              src="/assets/images/icons/diagnostics_button.svg"
              alt="View diagnostics"
              className="w-[20px] h-[20px]"
            />
          </button>
        </ToolTip>
        {screenshotComponent}

        <ToolTip placement="bottom" toolText="Edit Device Settings">
          <Link
            to={`/devices/${el.id}?edit`}
            type="button"
            className="duration-500 hover:scale-105 border-2 p-2 cursor-pointer ml-2 flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/pencil_button.svg"
              alt="Edit"
              className="w-[20px] h-[20px]"
            />
          </Link>
        </ToolTip>

        <ToolTip placement="bottom" toolText="Device Overview">
          <Link
            to={`/devices/${el.id}`}
            type="button"
            className="duration-500 hover:scale-105 cursor-pointer ml-2 border-2 p-2 flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/eye_device.svg"
              alt="View device content"
              className="w-[20px] h-[20px]"
            />
          </Link>
        </ToolTip>

        <ToolTip placement="bottom" toolText="Device Preview">
          <Link
            to={`/player/?device=${el.id}&campaign=${activeCampaignId}`}
            type="button"
            className="duration-500 hover:scale-105 cursor-pointer border-2 p-2 ml-2 flex justify-center items-center bg-white rounded-lg"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/assets/images/icons/black_play_button.svg"
              alt="Preview Device content"
              className="w-[20px] h-[20px]"
            />
          </Link>
        </ToolTip>
      </td>
    </tr>
  );
}

function DevicesTable(props) {
  const {
    auth,
    statusAndDevices,
    handleDeviceSelectedChange,
    handleDiagnosticsClicked,
    handleScreenshotClicked,
    activeCampaignId,
    getUptimeButton,
    goToDevicesPage,
    selectedDevices,
  } = props;

  return (
    <div className="border-2 py-8 mt-8 box-border px-4 bg-white rounded-[15px]">
      <table className="w-full block" data-test="devices-table">
        {/* Table HEADER */}
        <thead className=" th-hidden xl:w-full xl:!flex xl:h-[20px] h-0 w-0 overflow-hidden">
          <tr className="w-full flex">
            <th
              scope="col"
              className="poppins-bold w-[200px] text-left box-border px-2 text-dark-night"
            >
              Device Id
            </th>
            <th
              scope="col"
              className="poppins-bold w-[calc(28%-200px)] text-left box-border px-2 text-dark-night"
            >
              Device Name
            </th>
            <th
              scope="col"
              className="poppins-bold w-[20%] text-left box-border px-2 text-dark-night"
            >
              Location
            </th>
            <th
              scope="col"
              className="poppins-bold w-[14%] text-left box-border px-2 text-dark-night"
            >
              Player, Device, FW
            </th>
            <th
              scope="col"
              className="poppins-bold w-[16%] text-left box-border px-2 text-dark-night"
            >
              Status
            </th>
            <th
              scope="col"
              className="poppins-bold w-[24%] text-left box-border px-2 text-dark-night "
            >
              Edit
            </th>
          </tr>
        </thead>

        {/* Table Content */}
        <tbody className="w-full block">
          {statusAndDevices.map((el, i) =>
            getTableRows({
              el,
              i,
              auth,
              handleDeviceSelectedChange,
              handleDiagnosticsClicked,
              handleScreenshotClicked,
              activeCampaignId,
              getUptimeButton,
              goToDevicesPage,
              selectedDevices,
            }),
          )}
        </tbody>
      </table>
    </div>
  );
}

DevicesTable.propTypes = {
  auth: PropTypes.object.isRequired,
  statusAndDevices: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleDeviceSelectedChange: PropTypes.func.isRequired,
  handleDiagnosticsClicked: PropTypes.func.isRequired,
  handleScreenshotClicked: PropTypes.func.isRequired,
  activeCampaignId: PropTypes.number.isRequired,
  getUptimeButton: PropTypes.func.isRequired,
  goToDevicesPage: PropTypes.func.isRequired,
  selectedDevices: PropTypes.object.isRequired,
};

export default DevicesTable;
